<template>
  <RfInputWrapper
    ref="wrapperRef"
    :title="title"
    :requiredTitle="requiredTitle"
    :labelFor="`textarea${uid}`"
    :variant="variant"
    :errorMessage="errorMessage"
    :hintMessage="hintMessage"
    :counter="counter"
    :maxlength="maxlength"
    :value="input"
    :class="attrsFiltered.class"
  >
    <template v-if="slots.title" #title>
      <slot name="title" />
    </template>
    <textarea
      :id="`textarea${uid}`"
      ref="textareaRef"
      v-bind="attrsFiltered.rest"
      v-model="input"
      :disabled="disabled"
      :placeholder="placeholder"
      :rows="rows"
      :maxlength="maxlength"
      :aria-invalid="!!errorMessage"
      :aria-describedby="wrapperRef?.messageId || undefined"
      class="text-body placeholder:text-beige-50 w-full grow resize-none bg-transparent p-3 focus:outline-hidden focus-visible:ring-2 focus-visible:ring-blue-50"
      :class="{ 'text-beige-30': disabled, 'overflow-hidden': autoGrow }"
    />
  </RfInputWrapper>
</template>

<script setup>
import {
  computed,
  ref,
  useAttrs,
  getCurrentInstance,
  useSlots,
  watch,
  nextTick,
  onMounted,
  onBeforeUnmount,
} from "vue";
import RfInputWrapper from "@/components/inputs/RfInputWrapper.vue";

defineOptions({ inheritAttrs: false });

const { uid } = getCurrentInstance();
const slots = useSlots();
const wrapperRef = ref();
const textareaRef = ref();
const input = defineModel("modelValue", { type: String, default: "" });

const props = defineProps({
  placeholder: { type: String, default: "" },
  title: { type: String, default: "" },
  rows: { type: [String, Number], default: 4 },
  variant: { type: String, default: "default" },
  errorMessage: { type: String, default: "" },
  hintMessage: { type: String, default: "" },
  requiredTitle: { type: Boolean, default: false },
  disabled: { type: Boolean, default: false },
  autoGrow: { type: Boolean, default: false },
  counter: { type: [Boolean, String, Number], default: false },
  maxlength: { type: [String, Number], default: undefined },
});

const attrs = useAttrs();
const attrsFiltered = computed(() => {
  const { class: cssClass, ...rest } = attrs;
  return { rest, class: cssClass };
});

const resize = () => {
  if (!props.autoGrow || !textareaRef.value) return;
  textareaRef.value.style.height = "auto";
  textareaRef.value.style.height = `${textareaRef.value.scrollHeight}px`;
};

watch(input, () => nextTick(resize));
watch(
  () => props.autoGrow,
  () => nextTick(resize),
);

let resizeObserver;
onMounted(() => {
  resize();
  // The wrapper's box isn't always settled yet at mount (e.g. a parent panel
  // still animating open, or a layout pass still pending), which throws off
  // the scrollHeight read above and leaves it stuck too tall/short forever.
  // Re-measure whenever the wrapper's actual size changes.
  if (props.autoGrow && typeof ResizeObserver !== "undefined" && wrapperRef.value?.el) {
    resizeObserver = new ResizeObserver(resize);
    resizeObserver.observe(wrapperRef.value.el);
  }
});
onBeforeUnmount(() => resizeObserver?.disconnect());
</script>
